import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Bell, FileKey, History, Key, Lock,Shield, Smartphone, Users } from "lucide-react";
import { useState } from "react";

const mockAccessLogs = [
  {
    id: 1,
    user: "admin@example.com",
    action: "Вхід в систему",
    ip: "192.168.1.1",
    device: "Chrome on MacOS",
    timestamp: "2024-02-15T10:30:00",
    status: "success"
  },
  {
    id: 2,
    user: "manager@example.com",
    action: "Невдала спроба входу",
    ip: "192.168.1.2",
    device: "Firefox on Windows",
    timestamp: "2024-02-15T10:25:00",
    status: "failed"
  }
];

const mockPermissions = [
  {
    id: 1,
    role: "Адміністратор",
    permissions: ["read", "write", "delete", "manage_users"],
    users: 3
  },
  {
    id: 2,
    role: "Менеджер",
    permissions: ["read", "write"],
    users: 8
  },
  {
    id: 3,
    role: "Орендар",
    permissions: ["read"],
    users: 25
  }
];

const SecurityPage = () => {
  const [activeTab, setActiveTab] = useState("access-logs");
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [dataEncryption, setDataEncryption] = useState(true);
  const [backupEnabled, setBackupEnabled] = useState(true);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "success":
        return "bg-emerald-500/15 text-emerald-600";
      case "failed":
        return "bg-red-500/15 text-red-600";
      default:
        return "bg-slate-500/15 text-slate-600";
    }
  };

  return (
    <div className="container mx-auto p-8 space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Безпека системи</h1>
          <p className="text-muted-foreground">
            Керування безпекою та налаштування прав доступу
          </p>
        </div>
        <Button variant="outline" size="icon">
          <Bell className="h-5 w-5" />
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Активні сесії
            </CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">12</div>
            <p className="text-xs text-muted-foreground">
              За останні 24 години
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Спроби входу
            </CardTitle>
            <Key className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">45</div>
            <p className="text-xs text-muted-foreground">
              +12% від минулого тижня
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              2FA Активовано
            </CardTitle>
            <Smartphone className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">78%</div>
            <p className="text-xs text-muted-foreground">
              користувачів використовують
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Шифрування
            </CardTitle>
            <FileKey className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">100%</div>
            <p className="text-xs text-muted-foreground">
              даних захищено
            </p>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="access-logs" className="flex items-center gap-2">
            <History className="h-4 w-4" />
            Журнал доступу
          </TabsTrigger>
          <TabsTrigger value="permissions" className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            Права доступу
          </TabsTrigger>
          <TabsTrigger value="2fa" className="flex items-center gap-2">
            <Smartphone className="h-4 w-4" />
            2FA
          </TabsTrigger>
          <TabsTrigger value="encryption" className="flex items-center gap-2">
            <Lock className="h-4 w-4" />
            Шифрування
          </TabsTrigger>
        </TabsList>

        <TabsContent value="access-logs">
          <Card>
            <CardHeader>
              <CardTitle>Журнал доступу</CardTitle>
              <CardDescription>
                Історія входів та дій користувачів у системі
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Користувач</TableHead>
                      <TableHead>Дія</TableHead>
                      <TableHead>IP адреса</TableHead>
                      <TableHead>Пристрій</TableHead>
                      <TableHead>Час</TableHead>
                      <TableHead>Статус</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {mockAccessLogs.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell>{log.user}</TableCell>
                        <TableCell>{log.action}</TableCell>
                        <TableCell>{log.ip}</TableCell>
                        <TableCell>{log.device}</TableCell>
                        <TableCell>
                          {new Date(log.timestamp).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <Badge 
                            variant="secondary"
                            className={getStatusColor(log.status)}
                          >
                            {log.status === "success" ? "Успішно" : "Невдача"}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="permissions">
          <Card>
            <CardHeader>
              <CardTitle>Налаштування прав доступу</CardTitle>
              <CardDescription>
                Керування ролями та правами користувачів
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Роль</TableHead>
                      <TableHead>Права</TableHead>
                      <TableHead>Користувачів</TableHead>
                      <TableHead className="text-right">Дії</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {mockPermissions.map((role) => (
                      <TableRow key={role.id}>
                        <TableCell className="font-medium">
                          {role.role}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            {role.permissions.map((perm) => (
                              <Badge key={perm} variant="outline">
                                {perm}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell>{role.users}</TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm">
                            Редагувати
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="2fa">
          <Card>
            <CardHeader>
              <CardTitle>Двофакторна автентифікація</CardTitle>
              <CardDescription>
                Налаштування додаткового рівня безпеки для вашого облікового запису
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <h4 className="text-sm font-medium">
                    Двофакторна автентифікація
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    Увімкніть 2FA для додаткового захисту вашого облікового запису
                  </p>
                </div>
                <Switch
                  checked={twoFactorEnabled}
                  onCheckedChange={setTwoFactorEnabled}
                />
              </div>

              {twoFactorEnabled && (
                <div className="space-y-4">
                  <div className="rounded-lg border p-4">
                    <h4 className="text-sm font-medium mb-2">
                      Відскануйте QR-код
                    </h4>
                    <div className="bg-slate-100 w-48 h-48 rounded-lg mb-4" />
                    <p className="text-sm text-muted-foreground">
                      Використовуйте Google Authenticator або інший додаток для 2FA
                    </p>
                  </div>
                  <Button>Підтвердити налаштування</Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="encryption">
          <Card>
            <CardHeader>
              <CardTitle>Шифрування даних</CardTitle>
              <CardDescription>
                Налаштування шифрування та безпеки даних
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <h4 className="text-sm font-medium">
                    Шифрування даних при зберіганні
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    Всі чутливі дані будуть зашифровані перед збереженням
                  </p>
                </div>
                <Switch
                  checked={dataEncryption}
                  onCheckedChange={setDataEncryption}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <h4 className="text-sm font-medium">
                    Автоматичне резервне копіювання
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    Щоденне шифроване резервне копіювання всіх даних
                  </p>
                </div>
                <Switch
                  checked={backupEnabled}
                  onCheckedChange={setBackupEnabled}
                />
              </div>

              <div className="rounded-lg border p-4 space-y-4">
                <h4 className="text-sm font-medium">
                  Інформація про шифрування
                </h4>
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">
                    • Використовується AES-256 для шифрування даних
                  </p>
                  <p className="text-sm text-muted-foreground">
                    • SSL/TLS для захищеного з'єднання
                  </p>
                  <p className="text-sm text-muted-foreground">
                    • Ключі шифрування зберігаються окремо
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SecurityPage; 